/**
 * BRSR Module — SEBI Annexure II Format
 * Structured input for Section A (General Disclosures), Section B (Management & Process),
 * and Section C (Principle-wise Performance) matching the SEBI BRSR reporting format.
 *
 * Data is persisted to ReportMetadata (meta dict) and BRSRPrincipleData.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Save, RefreshCw, Download, Upload, Sparkles, ChevronRight, Plus, Trash2,
  Building2, Package, MapPin, Users, Briefcase, HandCoins, Eye,
  Shield, Leaf, Heart, UsersRound, Scale, TreePine, Landmark, Handshake, Star
} from 'lucide-react';
import { useAlert } from '../../context/AlertContext';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Reusable field components ──────────────────────────────────────────────

// Safely convert meta value to display string (handles objects/arrays/nested JSON)
const displayVal = (val) => {
  if (val === null || val === undefined) return '';
  if (typeof val !== 'object') return String(val);
  // Format objects/arrays as readable text
  if (Array.isArray(val)) {
    return val.map((item, i) => {
      if (typeof item === 'object' && item !== null) {
        return Object.entries(item)
          .filter(([, v]) => v !== null && v !== undefined && v !== '')
          .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
          .join(', ');
      }
      return String(item);
    }).join('\n');
  }
  // Plain object — format as "key: value" lines
  return Object.entries(val)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => {
      const label = k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      return `${label}: ${typeof v === 'object' ? JSON.stringify(v) : v}`;
    })
    .join('\n');
};

const Field = ({ label, field, meta, onChange, placeholder, required, type = 'text', className = '' }) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      value={displayVal(meta[field])}
      onChange={e => onChange(field, e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      placeholder={placeholder}
    />
  </div>
);

const TextArea = ({ label, field, meta, onChange, placeholder, rows = 3, className = '' }) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <textarea
      value={displayVal(meta[field])}
      onChange={e => onChange(field, e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      rows={rows}
      placeholder={placeholder}
    />
  </div>
);

const SectionHeading = ({ children }) => (
  <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-4">{children}</h3>
);

const QuestionLabel = ({ num, children }) => (
  <div className="mb-3">
    <span className="font-semibold text-gray-800 text-sm">{num}.</span>{' '}
    <span className="text-sm text-gray-700">{children}</span>
  </div>
);

// Dynamic table rows (array of objects)
const DynamicTable = ({ columns, dataKey, meta, onChange }) => {
  const rows = meta[dataKey] || [];
  const addRow = () => {
    const empty = {};
    columns.forEach(c => { empty[c.key] = ''; });
    onChange(dataKey, [...rows, empty]);
  };
  const removeRow = idx => {
    onChange(dataKey, rows.filter((_, i) => i !== idx));
  };
  const updateCell = (idx, key, val) => {
    const updated = rows.map((r, i) => i === idx ? { ...r, [key]: val } : r);
    onChange(dataKey, updated);
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border border-gray-200 mb-2">
        <thead>
          <tr className="bg-blue-50">
            <th className="border border-gray-200 px-2 py-1 text-left text-xs font-semibold text-gray-600 w-10">S.No.</th>
            {columns.map(c => (
              <th key={c.key} className="border border-gray-200 px-2 py-1 text-left text-xs font-semibold text-gray-600">
                {c.label}
              </th>
            ))}
            <th className="border border-gray-200 px-2 py-1 w-10"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx}>
              <td className="border border-gray-200 px-2 py-1 text-center text-xs text-gray-500">{idx + 1}</td>
              {columns.map(c => (
                <td key={c.key} className="border border-gray-200 px-1 py-1">
                  <input
                    type="text"
                    value={row[c.key] || ''}
                    onChange={e => updateCell(idx, c.key, e.target.value)}
                    className="w-full px-2 py-1 text-xs border-0 focus:ring-1 focus:ring-blue-400 rounded"
                    placeholder={c.placeholder || ''}
                  />
                </td>
              ))}
              <td className="border border-gray-200 px-1 py-1 text-center">
                <button onClick={() => removeRow(idx)} className="text-red-400 hover:text-red-600">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={addRow} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
        <Plus className="w-3.5 h-3.5" /> Add Row
      </button>
    </div>
  );
};

// Gender breakdown grid (S.No / Particulars / Total / Male No / Male% / Female No / Female%)
const GenderBreakdownGrid = ({ title, rows: rowDefs, prefix, meta, onChange }) => (
  <div className="mb-4">
    {title && <p className="text-sm font-semibold text-gray-700 mb-2">{title}</p>}
    <div className="overflow-x-auto">
      <table className="w-full text-xs border border-gray-200">
        <thead>
          <tr className="bg-blue-50">
            <th className="border border-gray-200 px-2 py-1 text-left">S.No.</th>
            <th className="border border-gray-200 px-2 py-1 text-left">Particulars</th>
            <th className="border border-gray-200 px-2 py-1 text-center">Total (A)</th>
            <th className="border border-gray-200 px-2 py-1 text-center" colSpan={2}>Male</th>
            <th className="border border-gray-200 px-2 py-1 text-center" colSpan={2}>Female</th>
          </tr>
          <tr className="bg-gray-50">
            <th className="border border-gray-200 px-2 py-1"></th>
            <th className="border border-gray-200 px-2 py-1"></th>
            <th className="border border-gray-200 px-2 py-1"></th>
            <th className="border border-gray-200 px-2 py-1 text-center">No. (B)</th>
            <th className="border border-gray-200 px-2 py-1 text-center">% (B/A)</th>
            <th className="border border-gray-200 px-2 py-1 text-center">No. (C)</th>
            <th className="border border-gray-200 px-2 py-1 text-center">% (C/A)</th>
          </tr>
        </thead>
        <tbody>
          {rowDefs.map((r, idx) => {
            if (r.separator) {
              return (
                <tr key={idx} className="bg-blue-50">
                  <td colSpan={7} className="border border-gray-200 px-2 py-1 font-bold text-center text-xs">{r.label}</td>
                </tr>
              );
            }
            const pfx = `${prefix}_${r.key}`;
            const fields = ['total', 'male_no', 'male_pct', 'female_no', 'female_pct'];
            return (
              <tr key={idx}>
                <td className="border border-gray-200 px-2 py-1 text-center">{r.sno}</td>
                <td className="border border-gray-200 px-2 py-1">{r.label}</td>
                {fields.map(f => (
                  <td key={f} className="border border-gray-200 px-1 py-1">
                    <input
                      type="text"
                      value={meta[`${pfx}_${f}`] || ''}
                      onChange={e => onChange(`${pfx}_${f}`, e.target.value)}
                      className="w-full px-1 py-0.5 text-xs border-0 text-center focus:ring-1 focus:ring-blue-400 rounded"
                    />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

// FY comparison grid (rows × Current FY / Previous FY)
const FYComparisonGrid = ({ label, rows: rowDefs, prefix, meta, onChange }) => (
  <div className="mb-4">
    <div className="overflow-x-auto">
      <table className="w-full text-xs border border-gray-200">
        <thead>
          <tr className="bg-blue-50">
            <th className="border border-gray-200 px-2 py-1 text-left">{label}</th>
            <th className="border border-gray-200 px-2 py-1 text-center">Current FY</th>
            <th className="border border-gray-200 px-2 py-1 text-center">Previous FY</th>
          </tr>
        </thead>
        <tbody>
          {rowDefs.map((r, idx) => (
            <tr key={idx}>
              <td className="border border-gray-200 px-2 py-1 text-sm">{r.label}</td>
              <td className="border border-gray-200 px-1 py-1">
                <input type="text" value={meta[`${prefix}_${r.key}_curr`] || ''} onChange={e => onChange(`${prefix}_${r.key}_curr`, e.target.value)}
                  className="w-full px-1 py-0.5 text-xs border-0 text-center focus:ring-1 focus:ring-blue-400 rounded" />
              </td>
              <td className="border border-gray-200 px-1 py-1">
                <input type="text" value={meta[`${prefix}_${r.key}_prev`] || ''} onChange={e => onChange(`${prefix}_${r.key}_prev`, e.target.value)}
                  className="w-full px-1 py-0.5 text-xs border-0 text-center focus:ring-1 focus:ring-blue-400 rounded" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Complaints grid (Category / Filed / Pending / Remarks × 2 FYs)
const ComplaintsGrid = ({ categories, prefix, meta, onChange }) => (
  <div className="overflow-x-auto mb-4">
    <table className="w-full text-xs border border-gray-200">
      <thead>
        <tr className="bg-blue-50">
          <th className="border border-gray-200 px-2 py-1 text-left" rowSpan={2}>Category</th>
          <th className="border border-gray-200 px-2 py-1 text-center" colSpan={3}>Current FY</th>
          <th className="border border-gray-200 px-2 py-1 text-center" colSpan={3}>Previous FY</th>
        </tr>
        <tr className="bg-gray-50">
          <th className="border border-gray-200 px-2 py-1 text-center">Filed</th>
          <th className="border border-gray-200 px-2 py-1 text-center">Pending</th>
          <th className="border border-gray-200 px-2 py-1 text-center">Remarks</th>
          <th className="border border-gray-200 px-2 py-1 text-center">Filed</th>
          <th className="border border-gray-200 px-2 py-1 text-center">Pending</th>
          <th className="border border-gray-200 px-2 py-1 text-center">Remarks</th>
        </tr>
      </thead>
      <tbody>
        {categories.map((cat, idx) => {
          const pfx = `${prefix}_${cat.key}`;
          return (
            <tr key={idx}>
              <td className="border border-gray-200 px-2 py-1">{cat.label}</td>
              {['filed_curr', 'pending_curr', 'remarks_curr', 'filed_prev', 'pending_prev', 'remarks_prev'].map(f => (
                <td key={f} className="border border-gray-200 px-1 py-1">
                  <input type="text" value={meta[`${pfx}_${f}`] || ''} onChange={e => onChange(`${pfx}_${f}`, e.target.value)}
                    className="w-full px-1 py-0.5 text-xs border-0 text-center focus:ring-1 focus:ring-blue-400 rounded" />
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// P1-P9 matrix (question rows × principle columns)
const PrincipleMatrixGrid = ({ questions, prefix, meta, onChange }) => (
  <div className="overflow-x-auto mb-4">
    <table className="w-full text-xs border border-gray-200">
      <thead>
        <tr className="bg-blue-50">
          <th className="border border-gray-200 px-2 py-1 text-left">Disclosure Questions</th>
          {[1,2,3,4,5,6,7,8,9].map(p => (
            <th key={p} className="border border-gray-200 px-1 py-1 text-center w-14">P{p}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {questions.map((q, idx) => (
          <tr key={idx}>
            <td className="border border-gray-200 px-2 py-1 text-xs">{q.label}</td>
            {[1,2,3,4,5,6,7,8,9].map(p => (
              <td key={p} className="border border-gray-200 px-0.5 py-0.5">
                <input type="text" value={meta[`${prefix}_${q.key}_p${p}`] || ''} onChange={e => onChange(`${prefix}_${q.key}_p${p}`, e.target.value)}
                  className="w-full px-0.5 py-0.5 text-xs border-0 text-center focus:ring-1 focus:ring-blue-400 rounded" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ── Navigation config ──────────────────────────────────────────────────────

const NAV_SECTIONS = [
  {
    id: 'section_a', label: 'Section A', subtitle: 'General Disclosures',
    icon: Building2,
    children: [
      { id: 'a_i', label: 'I. Entity Details', icon: Building2 },
      { id: 'a_ii', label: 'II. Products/Services', icon: Package },
      { id: 'a_iii', label: 'III. Operations', icon: MapPin },
      { id: 'a_iv', label: 'IV. Employees', icon: Users },
      { id: 'a_v', label: 'V. Holdings', icon: Briefcase },
      { id: 'a_vi', label: 'VI. CSR Details', icon: HandCoins },
      { id: 'a_vii', label: 'VII. Transparency', icon: Eye },
    ]
  },
  {
    id: 'section_b', label: 'Section B', subtitle: 'Management & Process',
    icon: Shield,
    children: [
      { id: 'b_policy', label: 'Policy Matrix (Q1-6)', icon: Shield },
      { id: 'b_governance', label: 'Governance (Q7-9)', icon: Landmark },
      { id: 'b_review', label: 'NGRBC Review (Q10-12)', icon: Eye },
    ]
  },
  {
    id: 'section_c', label: 'Section C', subtitle: 'Principle Performance',
    icon: Star,
    children: [
      { id: 'c_p1', label: 'P1: Ethics', icon: Scale },
      { id: 'c_p2', label: 'P2: Sustainability', icon: Leaf },
      { id: 'c_p3', label: 'P3: Well-being', icon: Heart },
      { id: 'c_p4', label: 'P4: Stakeholders', icon: UsersRound },
      { id: 'c_p5', label: 'P5: Human Rights', icon: Scale },
      { id: 'c_p6', label: 'P6: Environment', icon: TreePine },
      { id: 'c_p7', label: 'P7: Public Policy', icon: Landmark },
      { id: 'c_p8', label: 'P8: Inclusive Growth', icon: Handshake },
      { id: 'c_p9', label: 'P9: Customer Value', icon: Star },
    ]
  }
];

// ── Main component ─────────────────────────────────────────────────────────

const BRSRModule = ({ study }) => {
  const { showSuccess, showError, showInfo, confirm } = useAlert();
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingNarrative, setGeneratingNarrative] = useState(false);
  const [activePanel, setActivePanel] = useState('a_i');
  const [expandedSections, setExpandedSections] = useState({ section_a: true, section_b: false, section_c: false });

  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);

  // Also keep original indicator data for backward compat
  const [principles, setPrinciples] = useState([]);
  const [indicatorValues, setIndicatorValues] = useState({});

  useEffect(() => {
    if (study?.id) fetchData();
  }, [study?.id]);

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const [principlesRes, metadataRes] = await Promise.all([
        axios.get(`${API}/api/brsr/${study.id}/principles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/metadata/${study.id}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { metadata: {} } }))
      ]);

      const m = metadataRes.data?.metadata || metadataRes.data || {};
      setMeta(m);
      setPrinciples(principlesRes.data.principles || []);

      // Init indicator values
      const vals = {};
      principlesRes.data.principles?.forEach(p => {
        p.indicators?.forEach(ind => {
          if (ind.value !== undefined) vals[ind.id] = { value: ind.value, notes: ind.notes || '' };
        });
      });
      setIndicatorValues(vals);
    } catch (error) {
      console.error('Error fetching BRSR data:', error);
      showError('Failed to load BRSR data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = useCallback((field, value) => {
    setMeta(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      // Save metadata
      await axios.post(`${API}/api/metadata/${study.id}`, meta, { headers: { Authorization: `Bearer ${token}` } });

      // Also save indicator values (backward compat)
      const allIndicators = [];
      principles.forEach(principle => {
        principle.indicators.forEach(ind => {
          if (indicatorValues[ind.id]?.value) {
            allIndicators.push({
              principle_id: principle.id,
              indicator_name: ind.name,
              value: indicatorValues[ind.id]?.value || null,
              unit: ind.unit || 'Value',
              remarks: indicatorValues[ind.id]?.notes || null
            });
          }
        });
      });
      if (allIndicators.length > 0) {
        await axios.post(`${API}/api/brsr/${study.id}/data`, { indicators: allIndicators }, { headers: { Authorization: `Bearer ${token}` } });
      }

      showSuccess('BRSR data saved successfully!');
    } catch (error) {
      console.error('Error saving:', error);
      showError('Failed to save data');
    } finally {
      setSaving(false);
    }
  };

  const [reportProgress, setReportProgress] = useState({ progress: 0, message: '' });

  const generateAIPoweredReport = async () => {
    setGeneratingNarrative(true);
    setReportProgress({ progress: 0, message: 'Starting report generation...' });
    const token = localStorage.getItem('token');

    try {
      // Step 1: Generate narratives with SSE progress
      const response = await fetch(`${API}/api/brsr/${study.id}/generate-report-stream`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: 'Generation failed' }));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let pdfReady = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const evt = JSON.parse(line.substring(6));
              setReportProgress({ progress: evt.progress || 0, message: evt.message || '' });
              if (evt.stage === 'done' && evt.pdf_ready) {
                pdfReady = true;
              } else if (evt.stage === 'error') {
                throw new Error(evt.message);
              }
            } catch (e) {
              if (e.message && !e.message.includes('Unexpected end')) throw e;
            }
          }
        }
      }

      // Step 2: Download the PDF
      if (pdfReady) {
        setReportProgress({ progress: 98, message: 'Downloading PDF...' });
        const pdfResponse = await axios.get(
          `${API}/api/brsr/${study.id}/export/pdf?token=${token}`,
          { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }
        );
        const url = window.URL.createObjectURL(new Blob([pdfResponse.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `BRSR_Report_${study.name || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        showSuccess('AI-powered report generated and downloaded!');
      }
    } catch (error) {
      showError(error.message || 'Failed to generate report');
    } finally {
      setGeneratingNarrative(false);
      setReportProgress({ progress: 0, message: '' });
    }
  };

  const exportPDF = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/api/brsr/${study.id}/export/pdf?token=${token}`, { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BRSR_Report_${study.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      showError('Failed to export PDF');
    }
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/api/brsr/template/download`, { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'BRSR_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      showError('Failed to download template');
    }
  };

  const exportExcel = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/api/brsr/${study.id}/export/excel`, { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BRSR_Report_${study.id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      showError('Failed to export Excel');
    }
  };

  const handleExcelImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    event.target.value = ''; // reset input

    setUploadingExcel(true);
    setUploadStatus(null);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/api/brsr/${study.id}/import-excel`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` } }
      );
      setUploadStatus({
        status: 'success',
        message: `Imported ${response.data.fields_extracted} fields from ${response.data.sheets_processed.length} sheets`,
        sheets: response.data.sheets_processed,
      });
      // Reload metadata to reflect imported values
      if (response.data.metadata) {
        setMeta(response.data.metadata);
      } else {
        await fetchData();
      }
      showSuccess(`BRSR Excel imported: ${response.data.fields_extracted} fields from ${file.name}`);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Import failed';
      setUploadStatus({ status: 'error', message: detail });
      showError(detail);
    } finally {
      setUploadingExcel(false);
    }
  };

  const [scanningPdf, setScanningPdf] = useState(false);
  const [scanProgress, setScanProgress] = useState({ progress: 0, message: '' });

  const handlePdfScan = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    event.target.value = '';

    setScanningPdf(true);
    setScanProgress({ progress: 0, message: 'Uploading PDF...' });
    setUploadStatus(null);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Use fetch for SSE streaming (axios doesn't support SSE)
      const response = await fetch(`${API}/api/brsr/${study.id}/scan-pdf`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: 'Scan failed' }));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalData = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.substring(6));
              setScanProgress({ progress: event.progress || 0, message: event.message || '' });

              if (event.stage === 'done') {
                finalData = event;
              } else if (event.stage === 'error') {
                throw new Error(event.message);
              }
            } catch (parseErr) {
              if (parseErr.message !== 'Unexpected end of JSON input') {
                console.warn('SSE parse warning:', parseErr);
              }
            }
          }
        }
      }

      if (finalData) {
        setUploadStatus({
          status: 'success',
          message: `Scanned ${finalData.fields_extracted} fields from ${finalData.total_pages} pages`,
          sheets: finalData.sections_processed,
        });
        if (finalData.metadata) {
          setMeta(finalData.metadata);
        } else {
          await fetchData();
        }
        showSuccess(`PDF scanned: ${finalData.fields_extracted} fields from ${file.name}`);
      } else {
        setUploadStatus({ status: 'error', message: 'No data received from scan' });
        showError('Scan completed but no data received');
      }
    } catch (error) {
      const detail = error.message || 'PDF scan failed';
      setUploadStatus({ status: 'error', message: detail });
      showError(detail);
    } finally {
      setScanningPdf(false);
      setScanProgress({ progress: 0, message: '' });
    }
  };

  const handleReset = async () => {
    const confirmed = await confirm(
      'Reset All Fields',
      'Are you sure you want to clear ALL BRSR field values? This cannot be undone.',
      { confirmLabel: 'Reset All', destructive: true }
    );
    if (!confirmed) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/api/metadata/${study.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setMeta({});
      setIndicatorValues({});
      setUploadStatus(null);
      showSuccess('All BRSR fields cleared');
    } catch (error) {
      showError('Failed to reset fields');
    }
  };

  const toggleSection = id => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-3 text-lg">Loading BRSR Module...</span>
      </div>
    );
  }

  // ── Panel renderers ─────────────────────────────────────────────────────

  const renderPanel = () => {
    switch (activePanel) {
      case 'a_i': return renderSectionA_I();
      case 'a_ii': return renderSectionA_II();
      case 'a_iii': return renderSectionA_III();
      case 'a_iv': return renderSectionA_IV();
      case 'a_v': return renderSectionA_V();
      case 'a_vi': return renderSectionA_VI();
      case 'a_vii': return renderSectionA_VII();
      case 'b_policy': return renderSectionB_Policy();
      case 'b_governance': return renderSectionB_Governance();
      case 'b_review': return renderSectionB_Review();
      case 'c_p1': return renderPrinciple1();
      case 'c_p2': return renderPrinciple2();
      case 'c_p3': return renderPrinciple3();
      case 'c_p4': return renderPrinciple4();
      case 'c_p5': return renderPrinciple5();
      case 'c_p6': return renderPrinciple6();
      case 'c_p7': return renderPrinciple7();
      case 'c_p8': return renderPrinciple8();
      case 'c_p9': return renderPrinciple9();
      default: return renderSectionA_I();
    }
  };

  // ── Section A-I: Entity Details (Q1-15) ─────────────────────────────────

  const renderSectionA_I = () => (
    <div>
      <SectionHeading>I. Details of the Listed Entity (Q1-15)</SectionHeading>
      <div className="grid grid-cols-2 gap-4">
        <Field label="1. Corporate Identity Number (CIN)" field="cin" meta={meta} onChange={handleChange} placeholder="L12345MH2010PLC123456" required />
        <Field label="2. Name of the Listed Entity" field="company_name" meta={meta} onChange={handleChange} placeholder="ABC Limited" required />
        <Field label="3. Year of incorporation" field="year_of_incorporation" meta={meta} onChange={handleChange} placeholder="2010" />
        <div className="col-span-2">
          <TextArea label="4. Registered office address" field="registered_office_address" meta={meta} onChange={handleChange} placeholder="Full registered address" rows={2} />
        </div>
        <TextArea label="5. Corporate address" field="corporate_address" meta={meta} onChange={handleChange} placeholder="Corporate address (if different)" rows={2} className="col-span-2" />
        <Field label="6. E-mail" field="contact_email" meta={meta} onChange={handleChange} placeholder="brsr@company.com" type="email" />
        <Field label="7. Telephone" field="telephone" meta={meta} onChange={handleChange} placeholder="+91-22-12345678" />
        <Field label="8. Website" field="corporate_website" meta={meta} onChange={handleChange} placeholder="https://www.company.com" type="url" />
        <Field label="9. Financial year for reporting" field="financial_year" meta={meta} onChange={handleChange} placeholder="FY 2024-25" required />
        <Field label="10. Name of Stock Exchange(s)" field="stock_exchange" meta={meta} onChange={handleChange} placeholder="BSE, NSE" />
        <Field label="11. Paid-up Capital (INR)" field="paid_up_capital" meta={meta} onChange={handleChange} placeholder="50,00,00,000" />
        <Field label="12. Contact person for BRSR queries" field="contact_person_br" meta={meta} onChange={handleChange} placeholder="Name, Designation, Email, Phone" className="col-span-2" />
        <Field label="13. Reporting boundary (Standalone/Consolidated)" field="reporting_boundary" meta={meta} onChange={handleChange} placeholder="Standalone" />
        <Field label="14. Name of assurance provider" field="assurance_provider" meta={meta} onChange={handleChange} placeholder="Assurance provider name" />
        <Field label="15. Type of assurance obtained" field="assurance_type" meta={meta} onChange={handleChange} placeholder="Reasonable / Limited" />
        <Field label="Industry Sector" field="industry" meta={meta} onChange={handleChange} placeholder="Manufacturing" />
        <TextArea label="Product/Service Description" field="product_description" meta={meta} onChange={handleChange} placeholder="Brief description of main products/services..." rows={2} className="col-span-2" />
      </div>
    </div>
  );

  // ── Section A-II: Products/Services (Q16-17) ───────────────────────────

  const renderSectionA_II = () => (
    <div>
      <SectionHeading>II. Products / Services (Q16-17)</SectionHeading>

      <QuestionLabel num={16}>Details of business activities (accounting for 90% of the turnover):</QuestionLabel>
      <DynamicTable
        dataKey="sec_a_q16_activities"
        meta={meta}
        onChange={handleChange}
        columns={[
          { key: 'main_activity', label: 'Description of Main Activity', placeholder: 'e.g. Manufacturing' },
          { key: 'business_activity', label: 'Description of Business Activity', placeholder: 'e.g. Steel production' },
          { key: 'turnover_pct', label: '% of Turnover', placeholder: '45%' },
        ]}
      />

      <div className="mt-6">
        <QuestionLabel num={17}>Products/Services sold by the entity (accounting for 90% of turnover):</QuestionLabel>
        <DynamicTable
          dataKey="sec_a_q17_products"
          meta={meta}
          onChange={handleChange}
          columns={[
            { key: 'name', label: 'Product/Service', placeholder: 'e.g. Hot Rolled Coils' },
            { key: 'nic_code', label: 'NIC Code', placeholder: '24101' },
            { key: 'turnover_pct', label: '% of total Turnover', placeholder: '55%' },
          ]}
        />
      </div>
    </div>
  );

  // ── Section A-III: Operations (Q18-19) ──────────────────────────────────

  const renderSectionA_III = () => (
    <div>
      <SectionHeading>III. Operations (Q18-19)</SectionHeading>

      <QuestionLabel num={18}>Number of locations where plants and/or operations/offices are situated:</QuestionLabel>
      <div className="grid grid-cols-4 gap-3 mb-6">
        <div className="col-span-4 grid grid-cols-4 gap-3 bg-blue-50 p-2 rounded text-xs font-semibold text-gray-600">
          <span>Location</span><span>No. of plants</span><span>No. of offices</span><span>Total</span>
        </div>
        <span className="text-sm">National</span>
        <Field field="sec_a_q18_national_plants" meta={meta} onChange={handleChange} placeholder="0" label="" />
        <Field field="sec_a_q18_national_offices" meta={meta} onChange={handleChange} placeholder="0" label="" />
        <Field field="sec_a_q18_national_total" meta={meta} onChange={handleChange} placeholder="0" label="" />
        <span className="text-sm">International</span>
        <Field field="sec_a_q18_intl_plants" meta={meta} onChange={handleChange} placeholder="0" label="" />
        <Field field="sec_a_q18_intl_offices" meta={meta} onChange={handleChange} placeholder="0" label="" />
        <Field field="sec_a_q18_intl_total" meta={meta} onChange={handleChange} placeholder="0" label="" />
      </div>

      <QuestionLabel num={19}>Markets served by the entity:</QuestionLabel>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <Field label="a. National (No. of States)" field="sec_a_q19a_national_states" meta={meta} onChange={handleChange} placeholder="28" />
        <Field label="a. International (No. of Countries)" field="sec_a_q19a_intl_countries" meta={meta} onChange={handleChange} placeholder="12" />
      </div>
      <Field label="b. Contribution of exports as % of total turnover" field="sec_a_q19b_export_pct" meta={meta} onChange={handleChange} placeholder="25%" className="mb-4" />
      <TextArea label="c. Brief on types of customers" field="sec_a_q19c_customer_types" meta={meta} onChange={handleChange} placeholder="Describe customer segments..." rows={3} />
    </div>
  );

  // ── Section A-IV: Employees (Q20-22) ────────────────────────────────────

  const renderSectionA_IV = () => {
    const empRows = [
      { separator: true, label: 'EMPLOYEES' },
      { sno: 1, label: 'Permanent (D)', key: 'emp_perm' },
      { sno: 2, label: 'Other than Permanent (E)', key: 'emp_other' },
      { sno: 3, label: 'Total employees (D + E)', key: 'emp_total' },
      { separator: true, label: 'WORKERS' },
      { sno: 4, label: 'Permanent (F)', key: 'wrk_perm' },
      { sno: 5, label: 'Other than Permanent (G)', key: 'wrk_other' },
      { sno: 6, label: 'Total workers (F + G)', key: 'wrk_total' },
    ];

    const daRows = [
      { separator: true, label: 'DIFFERENTLY ABLED EMPLOYEES' },
      { sno: 1, label: 'Permanent (D)', key: 'da_emp_perm' },
      { sno: 2, label: 'Other than Permanent (E)', key: 'da_emp_other' },
      { sno: 3, label: 'Total differently abled employees (D + E)', key: 'da_emp_total' },
      { separator: true, label: 'DIFFERENTLY ABLED WORKERS' },
      { sno: 4, label: 'Permanent (F)', key: 'da_wrk_perm' },
      { sno: 5, label: 'Other than permanent (G)', key: 'da_wrk_other' },
      { sno: 6, label: 'Total differently abled workers (F + G)', key: 'da_wrk_total' },
    ];

    return (
      <div>
        <SectionHeading>IV. Employees (Q20-22)</SectionHeading>

        <QuestionLabel num={20}>Details as at the end of Financial Year:</QuestionLabel>
        <p className="text-sm font-semibold text-gray-600 mb-2">a. Employees and workers (including differently abled):</p>
        <GenderBreakdownGrid rows={empRows} prefix="sec_a_q20a" meta={meta} onChange={handleChange} />

        <p className="text-sm font-semibold text-gray-600 mb-2 mt-4">b. Differently abled Employees and workers:</p>
        <GenderBreakdownGrid rows={daRows} prefix="sec_a_q20b" meta={meta} onChange={handleChange} />

        <div className="mt-6">
          <QuestionLabel num={21}>Participation/Inclusion/Representation of women:</QuestionLabel>
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Board of Directors', key: 'bod' },
              { label: 'Key Management Personnel', key: 'kmp' },
            ].map(item => (
              <React.Fragment key={item.key}>
                <span className="text-sm self-center">{item.label}</span>
                <Field label={`Total (A)`} field={`sec_a_q21_${item.key}_total`} meta={meta} onChange={handleChange} placeholder="0" />
                <div className="grid grid-cols-2 gap-2">
                  <Field label="No. Female (B)" field={`sec_a_q21_${item.key}_female_no`} meta={meta} onChange={handleChange} placeholder="0" />
                  <Field label="% (B/A)" field={`sec_a_q21_${item.key}_female_pct`} meta={meta} onChange={handleChange} placeholder="0" />
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="mt-6">
          <QuestionLabel num={22}>Turnover rate for permanent employees and workers (past 3 years):</QuestionLabel>
          <p className="text-xs text-gray-500 mb-2">Enter Male / Female / Total for Current FY, Previous FY, and Year prior to the previous FY</p>
          {['perm_emp', 'perm_wrk'].map(cat => (
            <div key={cat} className="mb-3">
              <p className="text-sm font-semibold text-gray-600 mb-1">{cat === 'perm_emp' ? 'Permanent Employees' : 'Permanent Workers'}</p>
              <div className="grid grid-cols-9 gap-2">
                {['curr', 'prev', 'prior'].map(fy => (
                  <React.Fragment key={fy}>
                    <Field label={`${fy === 'curr' ? 'Curr' : fy === 'prev' ? 'Prev' : 'Prior'} Male`} field={`sec_a_q22_${cat}_${fy}_m`} meta={meta} onChange={handleChange} placeholder="0" />
                    <Field label="Female" field={`sec_a_q22_${cat}_${fy}_f`} meta={meta} onChange={handleChange} placeholder="0" />
                    <Field label="Total" field={`sec_a_q22_${cat}_${fy}_t`} meta={meta} onChange={handleChange} placeholder="0" />
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── Section A-V: Holdings (Q23) ─────────────────────────────────────────

  const renderSectionA_V = () => (
    <div>
      <SectionHeading>V. Holding, Subsidiary and Associate Companies (Q23)</SectionHeading>
      <QuestionLabel num={23}>Names of holding / subsidiary / associate companies / joint ventures:</QuestionLabel>
      <DynamicTable
        dataKey="sec_a_q23_holdings"
        meta={meta}
        onChange={handleChange}
        columns={[
          { key: 'name', label: 'Name of Company', placeholder: 'Company name' },
          { key: 'type', label: 'Type (Holding/Subsidiary/Associate/JV)', placeholder: 'Subsidiary' },
          { key: 'shares_pct', label: '% of shares held', placeholder: '51%' },
          { key: 'br_participation', label: 'Participates in BR? (Yes/No)', placeholder: 'Yes' },
        ]}
      />
      <TextArea label="Holdings summary (text fallback)" field="sec_a_q23_holdings_text" meta={meta} onChange={handleChange} placeholder="If no structured data above, describe holdings here..." rows={2} className="mt-3" />
    </div>
  );

  // ── Section A-VI: CSR Details (Q24) ─────────────────────────────────────

  const renderSectionA_VI = () => (
    <div>
      <SectionHeading>VI. CSR Details (Q24)</SectionHeading>
      <div className="grid grid-cols-2 gap-4">
        <Field label="(i) Whether CSR is applicable as per section 135 (Yes/No)" field="sec_a_q24_csr_applicable" meta={meta} onChange={handleChange} placeholder="Yes" />
        <Field label="(ii) Turnover (in Rs.)" field="total_revenue" meta={meta} onChange={handleChange} placeholder="1,000,000,000" />
        <Field label="(iii) Net worth (in Rs.)" field="net_worth" meta={meta} onChange={handleChange} placeholder="500,000,000" />
      </div>
    </div>
  );

  // ── Section A-VII: Transparency (Q25-26) ────────────────────────────────

  const renderSectionA_VII = () => (
    <div>
      <SectionHeading>VII. Transparency and Disclosures Compliances (Q25-26)</SectionHeading>

      <QuestionLabel num={25}>Complaints/Grievances on any of the principles (P1 to P9) under the NGRBC:</QuestionLabel>
      <ComplaintsGrid
        prefix="sec_a_q25"
        meta={meta}
        onChange={handleChange}
        categories={[
          { label: 'Communities', key: 'communities' },
          { label: 'Investors (other than shareholders)', key: 'investors' },
          { label: 'Shareholders', key: 'shareholders' },
          { label: 'Employees and workers', key: 'employees' },
          { label: 'Customers', key: 'customers' },
          { label: 'Value Chain Partners', key: 'value_chain' },
          { label: 'Other (please specify)', key: 'other' },
        ]}
      />

      <div className="mt-6">
        <QuestionLabel num={26}>Overview of the entity's material responsible business conduct issues:</QuestionLabel>
        <DynamicTable
          dataKey="sec_a_q26_material_issues"
          meta={meta}
          onChange={handleChange}
          columns={[
            { key: 'issue', label: 'Material issue identified', placeholder: 'Climate change' },
            { key: 'risk_or_opportunity', label: 'Risk or Opportunity (R/O)', placeholder: 'R' },
            { key: 'rationale', label: 'Rationale for identifying', placeholder: '' },
            { key: 'approach', label: 'Approach to adapt or mitigate', placeholder: '' },
            { key: 'financial_implications', label: 'Financial implications (+/-)', placeholder: '' },
          ]}
        />
        <TextArea label="Material issues summary (text fallback)" field="sec_a_q26_material_issues_text" meta={meta} onChange={handleChange} placeholder="If no structured data above, describe material ESG issues here..." rows={2} className="mt-3" />
      </div>
    </div>
  );

  // ── Section B: Policy Matrix ────────────────────────────────────────────

  const renderSectionB_Policy = () => (
    <div>
      <SectionHeading>Policy and management processes (Q1-6)</SectionHeading>
      <p className="text-sm text-gray-600 mb-4">Enter values for each principle (P1-P9). Use Yes/No or brief text.</p>
      <PrincipleMatrixGrid
        prefix="sec_b"
        meta={meta}
        onChange={handleChange}
        questions={[
          { label: '1a. Policy/policies cover each principle? (Yes/No)', key: 'q1a' },
          { label: '1b. Policy approved by Board? (Yes/No)', key: 'q1b' },
          { label: '1c. Web Link of the Policies', key: 'q1c' },
          { label: '2. Translated to procedures? (Yes/No)', key: 'q2' },
          { label: '3. Extend to value chain partners? (Yes/No)', key: 'q3' },
          { label: '4. Codes/certifications/standards adopted', key: 'q4' },
          { label: '5. Commitments, goals and targets', key: 'q5' },
          { label: '6. Performance against commitments', key: 'q6' },
        ]}
      />
    </div>
  );

  // ── Section B: Governance (Q7-9) ────────────────────────────────────────

  const renderSectionB_Governance = () => (
    <div>
      <SectionHeading>Governance, leadership and oversight (Q7-9)</SectionHeading>

      <QuestionLabel num={7}>Statement by director responsible for the business responsibility report:</QuestionLabel>
      <TextArea field="sec_b_q7_director_statement" meta={meta} onChange={handleChange} placeholder="Director's ESG statement highlighting challenges, targets and achievements..." rows={5} label="" />

      <div className="mt-6">
        <QuestionLabel num={8}>Details of the highest authority responsible for BR policy implementation:</QuestionLabel>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Name" field="sec_b_q8_name" meta={meta} onChange={handleChange} placeholder="Full name" />
          <Field label="Designation" field="sec_b_q8_designation" meta={meta} onChange={handleChange} placeholder="Managing Director" />
          <Field label="DIN (if applicable)" field="sec_b_q8_din" meta={meta} onChange={handleChange} placeholder="12345678" />
          <Field label="Telephone" field="sec_b_q8_telephone" meta={meta} onChange={handleChange} placeholder="+91-22-12345678" />
          <Field label="E-mail" field="sec_b_q8_email" meta={meta} onChange={handleChange} placeholder="director@company.com" className="col-span-2" />
        </div>
      </div>

      <div className="mt-6">
        <QuestionLabel num={9}>Does the entity have a sustainability committee? (Yes/No). Details:</QuestionLabel>
        <TextArea field="sec_b_q9_sustainability_committee" meta={meta} onChange={handleChange} placeholder="Provide committee details, members, meeting frequency..." rows={4} label="" />
      </div>
    </div>
  );

  // ── Section B: NGRBC Review (Q10-12) ────────────────────────────────────

  const renderSectionB_Review = () => (
    <div>
      <SectionHeading>NGRBC Review (Q10-12)</SectionHeading>

      <QuestionLabel num={10}>Details of Review of NGRBCs by the Company:</QuestionLabel>
      <p className="text-xs text-gray-500 mb-2">Performance against policies and follow up action:</p>
      <PrincipleMatrixGrid prefix="sec_b" meta={meta} onChange={handleChange} questions={[
        { label: 'Review undertaken by Director/Committee/Board?', key: 'q10_perf_review' },
        { label: 'Frequency (Annually/Half yearly/Quarterly/Other)', key: 'q10_perf_freq' },
      ]} />

      <p className="text-xs text-gray-500 mb-2 mt-4">Compliance with statutory requirements:</p>
      <PrincipleMatrixGrid prefix="sec_b" meta={meta} onChange={handleChange} questions={[
        { label: 'Review undertaken by Director/Committee/Board?', key: 'q10_comp_review' },
        { label: 'Frequency (Annually/Half yearly/Quarterly/Other)', key: 'q10_comp_freq' },
      ]} />

      <div className="mt-6">
        <QuestionLabel num={11}>Independent assessment by external agency? (Yes/No). Agency name:</QuestionLabel>
        <PrincipleMatrixGrid prefix="sec_b" meta={meta} onChange={handleChange} questions={[
          { label: 'Independent assessment? (Yes/No)', key: 'q11_assessment' },
          { label: 'Name of external agency', key: 'q11_agency' },
        ]} />
      </div>

      <div className="mt-6">
        <QuestionLabel num={12}>If no policy for some Principles, reasons:</QuestionLabel>
        <PrincipleMatrixGrid prefix="sec_b" meta={meta} onChange={handleChange} questions={[
          { label: 'Not material to business (Yes/No)', key: 'q12_not_material' },
          { label: 'Not ready to formulate/implement (Yes/No)', key: 'q12_not_ready' },
          { label: 'No resources available (Yes/No)', key: 'q12_no_resources' },
          { label: 'Planned for next FY (Yes/No)', key: 'q12_planned_next_fy' },
          { label: 'Other reason (specify)', key: 'q12_other' },
        ]} />
      </div>
    </div>
  );

  // ── Section C: Generic principle renderer ───────────────────────────────

  const renderPrincipleSection = (pNum, title, essentialFields, leadershipFields) => (
    <div>
      <SectionHeading>Principle {pNum}: {title}</SectionHeading>

      <div className="bg-blue-50 rounded-lg px-4 py-2 mb-4">
        <span className="text-xs font-bold text-blue-800">Essential Indicators</span>
      </div>
      {essentialFields}

      <div className="bg-purple-50 rounded-lg px-4 py-2 mb-4 mt-6">
        <span className="text-xs font-bold text-purple-800">Leadership Indicators</span>
      </div>
      {leadershipFields}
    </div>
  );

  // ── P1: Ethics ──────────────────────────────────────────────────────────

  const renderPrinciple1 = () => renderPrincipleSection(1,
    'Ethics, Transparency & Accountability',
    <>
      <QuestionLabel num={1}>Training and awareness programmes on Principles:</QuestionLabel>
      <FYComparisonGrid label="Segment" prefix="p1_e1" meta={meta} onChange={handleChange} rows={[
        { label: 'Board of Directors — No. of programmes', key: 'bod_count' },
        { label: 'Board of Directors — % of persons covered', key: 'bod_pct' },
        { label: 'KMPs — No. of programmes', key: 'kmp_count' },
        { label: 'KMPs — % of persons covered', key: 'kmp_pct' },
        { label: 'Employees — No. of programmes', key: 'employees_count' },
        { label: 'Employees — % of persons covered', key: 'employees_pct' },
        { label: 'Workers — No. of programmes', key: 'workers_count' },
        { label: 'Workers — % of persons covered', key: 'workers_pct' },
      ]} />

      <QuestionLabel num={2}>Fines / penalties / punishment / award details:</QuestionLabel>
      <TextArea field="p1_e2_fines_details" meta={meta} onChange={handleChange} placeholder="Details of monetary and non-monetary penalties..." rows={3} label="" />

      <QuestionLabel num={4}>Anti-corruption or anti-bribery policy details:</QuestionLabel>
      <TextArea field="p1_e4_anti_corruption" meta={meta} onChange={handleChange} placeholder="Policy brief and web-link..." rows={3} label="" />

      <QuestionLabel num={5}>Disciplinary action for bribery/corruption:</QuestionLabel>
      <FYComparisonGrid label="Category" prefix="p1_e5" meta={meta} onChange={handleChange} rows={[
        { label: 'Directors', key: 'directors' },
        { label: 'KMPs', key: 'kmps' },
        { label: 'Employees', key: 'employees' },
        { label: 'Workers', key: 'workers' },
      ]} />

      <QuestionLabel num={6}>Conflict of interest complaints:</QuestionLabel>
      <ComplaintsGrid prefix="p1_e6" meta={meta} onChange={handleChange} categories={[
        { label: 'Conflicts of Interest — Directors', key: 'dir' },
        { label: 'Conflicts of Interest — KMPs', key: 'kmp' },
      ]} />

      <QuestionLabel num={7}>Corrective actions on fines/penalties:</QuestionLabel>
      <TextArea field="p1_e7_corrective_actions" meta={meta} onChange={handleChange} placeholder="Details of corrective actions..." rows={3} label="" />

      <QuestionLabel num={8}>Number of days of accounts payables:</QuestionLabel>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <Field label="Current FY" field="p1_e8_ap_curr" meta={meta} onChange={handleChange} placeholder="45" />
        <Field label="Previous FY" field="p1_e8_ap_prev" meta={meta} onChange={handleChange} placeholder="42" />
      </div>

      <QuestionLabel num={9}>Open-ness of business (concentration of purchases/sales/trading):</QuestionLabel>
      <TextArea field="p1_e9_openness" meta={meta} onChange={handleChange} placeholder="Details of concentration of purchases among suppliers, sales among dealers/distributors, related party transactions..." rows={4} label="" />
    </>,
    <>
      <QuestionLabel num={1}>Awareness programmes for value chain partners:</QuestionLabel>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <Field label="Total programmes held" field="p1_l1_vc_count" meta={meta} onChange={handleChange} placeholder="5" />
        <Field label="Topics covered" field="p1_l1_vc_topics" meta={meta} onChange={handleChange} placeholder="Ethics, Anti-bribery" />
        <Field label="% of VC partners covered" field="p1_l1_vc_pct" meta={meta} onChange={handleChange} placeholder="60%" />
      </div>

      <QuestionLabel num={2}>Processes to manage Board conflict of interest? (Yes/No). Details:</QuestionLabel>
      <TextArea field="p1_l2_coi_process" meta={meta} onChange={handleChange} placeholder="Details of COI management process..." rows={3} label="" />
    </>
  );

  // ── P2: Sustainability ──────────────────────────────────────────────────

  const renderPrinciple2 = () => renderPrincipleSection(2,
    'Sustainable Products & Services',
    <>
      <QuestionLabel num={1}>R&D and capex investments for environmental/social improvements:</QuestionLabel>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <Field label="R&D — Current FY (%)" field="p2_e1_rd_curr" meta={meta} onChange={handleChange} placeholder="2.5%" />
        <Field label="R&D — Previous FY (%)" field="p2_e1_rd_prev" meta={meta} onChange={handleChange} placeholder="2.0%" />
        <Field label="Capex — Current FY (%)" field="p2_e1_capex_curr" meta={meta} onChange={handleChange} placeholder="5%" />
        <Field label="Capex — Previous FY (%)" field="p2_e1_capex_prev" meta={meta} onChange={handleChange} placeholder="4%" />
      </div>
      <TextArea label="Details of improvements" field="p2_e1_rd_details" meta={meta} onChange={handleChange} placeholder="Describe environmental and social improvements..." rows={3} />

      <QuestionLabel num={2}>Sustainable sourcing:</QuestionLabel>
      <Field label="a. Procedures for sustainable sourcing? (Yes/No)" field="p2_e2a_sustainable_sourcing" meta={meta} onChange={handleChange} placeholder="Yes" className="mb-2" />
      <Field label="b. % of inputs sourced sustainably" field="p2_e2b_sustainable_pct" meta={meta} onChange={handleChange} placeholder="30%" className="mb-4" />

      <QuestionLabel num={3}>Processes to reclaim products at end of life:</QuestionLabel>
      <TextArea field="p2_e3_reclaim_processes" meta={meta} onChange={handleChange} placeholder="Describe processes for Plastics, E-waste, Hazardous waste, Other waste..." rows={3} label="" />

      <QuestionLabel num={4}>Extended Producer Responsibility (EPR) applicable? (Yes/No). Details:</QuestionLabel>
      <TextArea field="p2_e4_epr" meta={meta} onChange={handleChange} placeholder="EPR details and compliance..." rows={3} label="" />
    </>,
    <>
      <QuestionLabel num={1}>Life Cycle Assessment (LCA) conducted? If yes, details:</QuestionLabel>
      <TextArea field="p2_l1_lca_text" meta={meta} onChange={handleChange} placeholder="LCA details — products assessed, boundary, external agency, public results..." rows={3} label="" />

      <QuestionLabel num={2}>Environmental concerns from LCA, actions taken:</QuestionLabel>
      <TextArea field="p2_l2_env_concerns" meta={meta} onChange={handleChange} placeholder="Significant concerns and mitigation actions..." rows={3} label="" />

      <QuestionLabel num={3}>% of recycled/reused input material:</QuestionLabel>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <Field label="Input material" field="p2_l3_material" meta={meta} onChange={handleChange} placeholder="Steel scrap" />
        <Field label="Current FY (%)" field="p2_l3_curr" meta={meta} onChange={handleChange} placeholder="15%" />
        <Field label="Previous FY (%)" field="p2_l3_prev" meta={meta} onChange={handleChange} placeholder="12%" />
      </div>

      <QuestionLabel num={4}>Products/packaging reclaimed at end of life:</QuestionLabel>
      <TextArea field="p2_l4_reclaimed" meta={meta} onChange={handleChange} placeholder="Details of products and packaging reclaimed at end of life (plastics, e-waste, hazardous waste, other)..." rows={3} label="" />

      <QuestionLabel num={5}>Reclaimed products as % of products sold:</QuestionLabel>
      <Field label="Reclaimed %" field="p2_l5_reclaimed_pct" meta={meta} onChange={handleChange} placeholder="5%" className="mb-4" />
    </>
  );

  // ── P3: Well-being ──────────────────────────────────────────────────────

  const renderPrinciple3 = () => renderPrincipleSection(3,
    'Employee Well-being',
    <>
      <QuestionLabel num={1}>Well-being measures for employees and workers:</QuestionLabel>
      <TextArea field="p3_e1_wellbeing_details" meta={meta} onChange={handleChange} placeholder="Details of health insurance, accident insurance, maternity/paternity benefits, day care facilities coverage..." rows={4} label="" />

      <QuestionLabel num={2}>Retirement benefits (PF, Gratuity, ESI):</QuestionLabel>
      <TextArea field="p3_e2_retirement_details" meta={meta} onChange={handleChange} placeholder="No. of employees/workers covered, % coverage, deposited with authority (Y/N)..." rows={3} label="" />

      <QuestionLabel num={3}>Accessibility of workplaces to differently abled:</QuestionLabel>
      <TextArea field="p3_e3_accessibility" meta={meta} onChange={handleChange} placeholder="Yes/No and steps taken..." rows={2} label="" />

      <QuestionLabel num={5}>Return to work and Retention rates after parental leave:</QuestionLabel>
      <TextArea field="p3_e5_parental_leave" meta={meta} onChange={handleChange} placeholder="Return to work rate and retention rate by gender..." rows={3} label="" />

      <QuestionLabel num={6}>Grievance mechanism for employees and workers:</QuestionLabel>
      <TextArea field="p3_e6_grievance" meta={meta} onChange={handleChange} placeholder="Details of grievance redressal mechanism for permanent workers, other workers, permanent employees, other employees..." rows={3} label="" />

      <QuestionLabel num={7}>Union/association membership details:</QuestionLabel>
      <TextArea field="p3_e7_union_membership" meta={meta} onChange={handleChange} placeholder="Details of union membership for employees and workers (total, male/female, % covered)..." rows={3} label="" />

      <QuestionLabel num={8}>Training details (Health & Safety and Skill upgradation):</QuestionLabel>
      <TextArea field="p3_e8_training_details" meta={meta} onChange={handleChange} placeholder="Details of training on H&S measures and skill upgradation for employees and workers..." rows={3} label="" />

      <QuestionLabel num={9}>Performance review details:</QuestionLabel>
      <TextArea field="p3_e9_performance_reviews" meta={meta} onChange={handleChange} placeholder="Details of performance and career development reviews for employees and workers..." rows={3} label="" />

      <QuestionLabel num={10}>Health and safety management system:</QuestionLabel>
      <TextArea field="p3_e10a_ohsms" meta={meta} onChange={handleChange} placeholder="a. Occupational H&S system implemented? (Yes/No), coverage..." rows={2} label="" />

      <QuestionLabel num={11}>Safety related incidents:</QuestionLabel>
      <FYComparisonGrid label="Safety Incident" prefix="p3_e11" meta={meta} onChange={handleChange} rows={[
        { label: 'LTIFR — Employees', key: 'ltifr_emp' },
        { label: 'LTIFR — Workers', key: 'ltifr_wrk' },
        { label: 'Total injuries — Employees', key: 'injuries_emp' },
        { label: 'Total injuries — Workers', key: 'injuries_wrk' },
        { label: 'No. of fatalities — Employees', key: 'fatalities_emp' },
        { label: 'No. of fatalities — Workers', key: 'fatalities_wrk' },
      ]} />

      <QuestionLabel num={13}>Complaints by employees and workers:</QuestionLabel>
      <ComplaintsGrid prefix="p3_e13" meta={meta} onChange={handleChange} categories={[
        { label: 'Working Conditions', key: 'wc' },
        { label: 'Health & Safety', key: 'hs' },
      ]} />

      <QuestionLabel num={14}>Assessments for the year (health & safety, working conditions):</QuestionLabel>
      <TextArea field="p3_e14_assessments" meta={meta} onChange={handleChange} placeholder="Details of assessments done — health and safety practices, working conditions..." rows={3} label="" />
    </>,
    <>
      <QuestionLabel num={1}>Life insurance / compensatory package (Employees Y/N, Workers Y/N):</QuestionLabel>
      <TextArea field="p3_l1_life_insurance" meta={meta} onChange={handleChange} placeholder="Details..." rows={2} label="" />
      <QuestionLabel num={4}>Transition assistance programs? (Yes/No):</QuestionLabel>
      <TextArea field="p3_l4_transition" meta={meta} onChange={handleChange} placeholder="Details..." rows={2} label="" />
      <QuestionLabel num={5}>Value chain partner assessments (health & safety, working conditions):</QuestionLabel>
      <TextArea field="p3_l5_vc_assessment" meta={meta} onChange={handleChange} placeholder="Details of assessments done for value chain partners..." rows={2} label="" />
    </>
  );

  // ── P4: Stakeholders ────────────────────────────────────────────────────

  const renderPrinciple4 = () => renderPrincipleSection(4,
    'Stakeholder Engagement',
    <>
      <QuestionLabel num={1}>Processes for identifying key stakeholder groups:</QuestionLabel>
      <TextArea field="p4_e1_stakeholder_process" meta={meta} onChange={handleChange} placeholder="Describe stakeholder identification process..." rows={3} label="" />

      <QuestionLabel num={2}>List stakeholder groups with engagement details:</QuestionLabel>
      <DynamicTable dataKey="p4_e2_stakeholder_groups" meta={meta} onChange={handleChange} columns={[
        { key: 'group', label: 'Stakeholder Group', placeholder: 'Employees' },
        { key: 'vulnerable', label: 'Vulnerable/Marginalized? (Yes/No)', placeholder: 'No' },
        { key: 'channels', label: 'Channels of communication', placeholder: 'Town halls, surveys' },
        { key: 'frequency', label: 'Frequency', placeholder: 'Quarterly' },
        { key: 'purpose', label: 'Purpose and scope', placeholder: 'Feedback on policies' },
      ]} />
    </>,
    <>
      <QuestionLabel num={1}>Processes for Board consultation with stakeholders:</QuestionLabel>
      <TextArea field="p4_l1_board_consultation" meta={meta} onChange={handleChange} placeholder="Details..." rows={3} label="" />
      <QuestionLabel num={2}>Stakeholder consultation for environmental/social topics? (Yes/No). Details:</QuestionLabel>
      <TextArea field="p4_l2_env_social_topics" meta={meta} onChange={handleChange} placeholder="Details..." rows={3} label="" />
    </>
  );

  // ── P5: Human Rights ────────────────────────────────────────────────────

  const renderPrinciple5 = () => renderPrincipleSection(5,
    'Human Rights',
    <>
      <QuestionLabel num={1}>Employees/workers trained on human rights:</QuestionLabel>
      <TextArea field="p5_e1_training_details" meta={meta} onChange={handleChange} placeholder="Total, No. trained, % for employees (permanent/other) and workers..." rows={3} label="" />

      <QuestionLabel num={2}>Minimum wages paid details:</QuestionLabel>
      <TextArea field="p5_e2_minimum_wages" meta={meta} onChange={handleChange} placeholder="Details of minimum wages compliance..." rows={3} label="" />

      <QuestionLabel num={3}>Remuneration/salary details (gross wages, median remuneration):</QuestionLabel>
      <TextArea field="p5_e3_remuneration" meta={meta} onChange={handleChange} placeholder="Details of remuneration/salary/wages by gender (BoD, KMP, employees, workers)..." rows={3} label="" />

      <QuestionLabel num={4}>Focal point for human rights impacts? (Yes/No). Details:</QuestionLabel>
      <TextArea field="p5_e4_focal_point" meta={meta} onChange={handleChange} placeholder="Details..." rows={2} label="" />

      <QuestionLabel num={6}>Complaints on human rights:</QuestionLabel>
      <ComplaintsGrid prefix="p5_e6" meta={meta} onChange={handleChange} categories={[
        { label: 'Sexual Harassment', key: 'sh' },
        { label: 'Discrimination at workplace', key: 'discrimination' },
        { label: 'Child Labour', key: 'child_labour' },
        { label: 'Forced Labour/Involuntary Labour', key: 'forced_labour' },
        { label: 'Wages', key: 'wages' },
        { label: 'Other human rights issues', key: 'other' },
      ]} />

      <QuestionLabel num={7}>POSH complaints summary:</QuestionLabel>
      <TextArea field="p5_e7_posh" meta={meta} onChange={handleChange} placeholder="Complaints filed under Sexual Harassment of Women at Workplace Act — total, % disposed, upheld..." rows={3} label="" />

      <QuestionLabel num={10}>Assessments for the year (human rights):</QuestionLabel>
      <TextArea field="p5_e10_assessments" meta={meta} onChange={handleChange} placeholder="Details of assessments done on child labour, forced labour, sexual harassment, discrimination, wages..." rows={3} label="" />

      <QuestionLabel num={11}>Corrective actions taken from assessments:</QuestionLabel>
      <TextArea field="p5_e11_corrective_action" meta={meta} onChange={handleChange} placeholder="Details of corrective actions taken based on assessment findings..." rows={3} label="" />
    </>,
    <>
      <QuestionLabel num={1}>Business process modified due to HR grievances:</QuestionLabel>
      <TextArea field="p5_l1_process_modified" meta={meta} onChange={handleChange} placeholder="Details..." rows={3} label="" />
      <QuestionLabel num={2}>Human rights due-diligence scope and coverage:</QuestionLabel>
      <TextArea field="p5_l2_due_diligence" meta={meta} onChange={handleChange} placeholder="Details..." rows={3} label="" />
      <QuestionLabel num={3}>Accessibility for differently abled:</QuestionLabel>
      <TextArea field="p5_l3_accessibility" meta={meta} onChange={handleChange} placeholder="Details of accessibility measures for differently abled persons..." rows={2} label="" />
      <QuestionLabel num={4}>Value chain assessment (human rights):</QuestionLabel>
      <TextArea field="p5_l4_vc_assessment" meta={meta} onChange={handleChange} placeholder="Details of human rights assessments for value chain partners..." rows={2} label="" />
      <QuestionLabel num={5}>Value chain corrective actions:</QuestionLabel>
      <TextArea field="p5_l5_vc_corrective" meta={meta} onChange={handleChange} placeholder="Details of corrective actions taken for value chain partners..." rows={2} label="" />
    </>
  );

  // ── P6: Environment ─────────────────────────────────────────────────────

  const renderPrinciple6 = () => renderPrincipleSection(6,
    'Environmental Stewardship',
    <>
      <QuestionLabel num={1}>Energy consumption details:</QuestionLabel>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <Field label="Electricity consumption (kWh)" field="electricity_consumption" meta={meta} onChange={handleChange} placeholder="1,000,000" />
        <Field label="Renewable energy share (%)" field="renewable_energy_share" meta={meta} onChange={handleChange} placeholder="25" />
        <Field label="Diesel consumption (Litres)" field="fuel_consumption_diesel" meta={meta} onChange={handleChange} placeholder="50,000" />
      </div>

      <QuestionLabel num={3}>Water disclosures:</QuestionLabel>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <Field label="Water Withdrawal (KL)" field="water_withdrawal" meta={meta} onChange={handleChange} placeholder="10,000" />
        <Field label="Water Discharge (KL)" field="water_discharge" meta={meta} onChange={handleChange} placeholder="8,000" />
        <Field label="Water Recycled (%)" field="water_recycled_percent" meta={meta} onChange={handleChange} placeholder="30" />
      </div>

      <QuestionLabel num={4}>Air emissions details:</QuestionLabel>
      <TextArea field="p6_e4_air_emissions" meta={meta} onChange={handleChange} placeholder="Details of air emissions — NOx, SOx, PM, POP, VOC, HAP, heavy metals, others (in metric tonnes)..." rows={3} label="" />

      <QuestionLabel num={5}>GHG emissions:</QuestionLabel>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <Field label="Scope 1 Emissions (tCO2e)" field="scope1_emissions" meta={meta} onChange={handleChange} placeholder="1,234" />
        <Field label="Scope 2 Emissions (tCO2e)" field="scope2_emissions" meta={meta} onChange={handleChange} placeholder="5,678" />
        <Field label="Scope 3 Emissions (tCO2e)" field="scope3_categories" meta={meta} onChange={handleChange} placeholder="9,012" />
        <Field label="Emissions Intensity (tCO2e/unit)" field="emissions_intensity" meta={meta} onChange={handleChange} placeholder="0.5" />
      </div>

      <QuestionLabel num={6}>GHG reduction projects:</QuestionLabel>
      <TextArea field="p6_e6_ghg_reduction" meta={meta} onChange={handleChange} placeholder="Details of projects to reduce GHG emissions..." rows={3} label="" />

      <QuestionLabel num={7}>Waste management:</QuestionLabel>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <Field label="Hazardous Waste (tonnes)" field="waste_hazardous" meta={meta} onChange={handleChange} placeholder="50" />
        <Field label="Non-Hazardous Waste (tonnes)" field="waste_non_hazardous" meta={meta} onChange={handleChange} placeholder="500" />
        <Field label="Waste Recycled (%)" field="waste_recycled_percent" meta={meta} onChange={handleChange} placeholder="60" />
      </div>

      <QuestionLabel num={8}>Waste management details:</QuestionLabel>
      <TextArea field="p6_e8_waste_management" meta={meta} onChange={handleChange} placeholder="Details of waste management — generation, disposal, recycling, recovery methods..." rows={3} label="" />

      <QuestionLabel num={12}>Compliance with environmental law/regulations:</QuestionLabel>
      <TextArea field="p6_e12_compliance" meta={meta} onChange={handleChange} placeholder="Yes/No. If not, details of fines/penalties..." rows={3} label="" />
    </>,
    <>
      <QuestionLabel num={1}>Renewable vs non-renewable energy break-up:</QuestionLabel>
      <TextArea field="p6_l1_energy_breakup" meta={meta} onChange={handleChange} placeholder="Details of energy sources..." rows={3} label="" />
      <QuestionLabel num={4}>Scope 3 emissions details:</QuestionLabel>
      <TextArea field="p6_l4_scope3" meta={meta} onChange={handleChange} placeholder="Scope 3 categories and values..." rows={3} label="" />
    </>
  );

  // ── P7: Public Policy ───────────────────────────────────────────────────

  const renderPrinciple7 = () => renderPrincipleSection(7,
    'Public Policy',
    <>
      <QuestionLabel num={1}>Trade and industry chambers/associations:</QuestionLabel>
      <Field label="a. Number of affiliations" field="p7_e1a_affiliations_count" meta={meta} onChange={handleChange} placeholder="5" className="mb-4" />
      <p className="text-sm text-gray-600 mb-2">b. Top 10 associations:</p>
      <DynamicTable dataKey="p7_e1b_associations" meta={meta} onChange={handleChange} columns={[
        { key: 'name', label: 'Name of chamber/association', placeholder: 'CII' },
        { key: 'reach', label: 'Reach (State/National/International)', placeholder: 'National' },
      ]} />

      <div className="mt-4">
        <QuestionLabel num={2}>Anti-competitive conduct — corrective actions:</QuestionLabel>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Name of authority" field="p7_e2_authority" meta={meta} onChange={handleChange} placeholder="CCI" />
          <Field label="Brief of the case" field="p7_e2_case_brief" meta={meta} onChange={handleChange} placeholder="" />
          <Field label="Corrective action taken" field="p7_e2_corrective_action" meta={meta} onChange={handleChange} placeholder="" />
        </div>
      </div>
    </>,
    <>
      <QuestionLabel num={1}>Public policy positions advocated:</QuestionLabel>
      <TextArea field="p7_l1_public_policy" meta={meta} onChange={handleChange} placeholder="Details of public policy positions..." rows={3} label="" />
    </>
  );

  // ── P8: Inclusive Growth ─────────────────────────────────────────────────

  const renderPrinciple8 = () => renderPrincipleSection(8,
    'Inclusive Growth',
    <>
      <QuestionLabel num={1}>Social Impact Assessments (SIA) details:</QuestionLabel>
      <TextArea field="p8_e1_sia" meta={meta} onChange={handleChange} placeholder="Details of SIA conducted..." rows={3} label="" />
      <QuestionLabel num={2}>Rehabilitation and Resettlement (R&R) projects:</QuestionLabel>
      <TextArea field="p8_e2_randr" meta={meta} onChange={handleChange} placeholder="Details of ongoing R&R..." rows={3} label="" />
      <QuestionLabel num={3}>Community grievance mechanisms:</QuestionLabel>
      <TextArea field="p8_e3_community_grievance" meta={meta} onChange={handleChange} placeholder="Details..." rows={3} label="" />
      <QuestionLabel num={4}>% of inputs sourced from MSMEs/local suppliers:</QuestionLabel>
      <FYComparisonGrid label="Source" prefix="p8_e4" meta={meta} onChange={handleChange} rows={[
        { label: 'Directly from MSMEs/small producers', key: 'msme' },
        { label: 'From within district and neighbouring districts', key: 'local' },
      ]} />
    </>,
    <>
      <QuestionLabel num={2}>CSR project details with amounts:</QuestionLabel>
      <TextArea field="p8_l2_csr_details" meta={meta} onChange={handleChange} placeholder="CSR projects, beneficiaries, amounts..." rows={3} label="" />
      <QuestionLabel num={3}>Preferential procurement policy? (Yes/No). Details:</QuestionLabel>
      <TextArea field="p8_l3_preferential_procurement" meta={meta} onChange={handleChange} placeholder="Details..." rows={3} label="" />
      <QuestionLabel num={4}>Intellectual property benefits shared with communities:</QuestionLabel>
      <TextArea field="p8_l4_ip_benefits" meta={meta} onChange={handleChange} placeholder="Details of IP-based benefits shared with communities..." rows={2} label="" />
    </>
  );

  // ── P9: Customer Value ──────────────────────────────────────────────────

  const renderPrinciple9 = () => renderPrincipleSection(9,
    'Customer Value',
    <>
      <QuestionLabel num={1}>Mechanism to receive and respond to consumer complaints:</QuestionLabel>
      <TextArea field="p9_e1_complaint_mechanism" meta={meta} onChange={handleChange} placeholder="Details of complaint mechanism..." rows={3} label="" />

      <QuestionLabel num={2}>Product information — turnover percentage:</QuestionLabel>
      <TextArea field="p9_e2_product_info" meta={meta} onChange={handleChange} placeholder="Turnover percentage of products/services with information on environmental and social parameters, safe and responsible usage, recycling..." rows={3} label="" />

      <QuestionLabel num={3}>Consumer complaints:</QuestionLabel>
      <ComplaintsGrid prefix="p9_e3" meta={meta} onChange={handleChange} categories={[
        { label: 'Data privacy', key: 'data_privacy' },
        { label: 'Advertising', key: 'advertising' },
        { label: 'Cyber-security', key: 'cyber_security' },
        { label: 'Delivery of essential services', key: 'essential_services' },
        { label: 'Restrictive Trade Practices', key: 'restrictive_trade' },
        { label: 'Unfair Trade Practices', key: 'unfair_trade' },
        { label: 'Other', key: 'other' },
      ]} />

      <QuestionLabel num={4}>Product recalls:</QuestionLabel>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <Field label="Voluntary recalls" field="p9_e4_voluntary_recalls" meta={meta} onChange={handleChange} placeholder="0" />
        <Field label="Forced recalls" field="p9_e4_forced_recalls" meta={meta} onChange={handleChange} placeholder="0" />
      </div>

      <QuestionLabel num={5}>Cyber security and data privacy framework? (Yes/No). Details:</QuestionLabel>
      <TextArea field="p9_e5_cyber_security" meta={meta} onChange={handleChange} placeholder="Details of cyber security policy/framework..." rows={3} label="" />
    </>,
    <>
      <QuestionLabel num={1}>Information channels for products/services:</QuestionLabel>
      <TextArea field="p9_l1_info_channels" meta={meta} onChange={handleChange} placeholder="Channels / platforms..." rows={2} label="" />
      <QuestionLabel num={2}>Steps to educate consumers on safe usage:</QuestionLabel>
      <TextArea field="p9_l2_consumer_education" meta={meta} onChange={handleChange} placeholder="Details..." rows={2} label="" />
      <QuestionLabel num={3}>Disruption mechanisms for product/service availability:</QuestionLabel>
      <TextArea field="p9_l3_disruption_mechanism" meta={meta} onChange={handleChange} placeholder="Details of mechanisms to address disruptions in availability of products/services..." rows={2} label="" />
      <QuestionLabel num={4}>Product information display and labelling:</QuestionLabel>
      <TextArea field="p9_l4_product_info" meta={meta} onChange={handleChange} placeholder="Details of product information displayed — environmental/social parameters, recycling info, safe usage..." rows={2} label="" />
      <QuestionLabel num={5}>Consumer satisfaction survey conducted? (Yes/No). Details:</QuestionLabel>
      <TextArea field="p9_l5_consumer_survey" meta={meta} onChange={handleChange} placeholder="Details of survey..." rows={2} label="" />
    </>
  );

  // ── RENDER ──────────────────────────────────────────────────────────────

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-slate-600 rounded-lg shadow-lg p-6 mb-6 text-white">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold mb-2">BRSR — SEBI Annexure II Format</h1>
            <p className="text-blue-100">Business Responsibility & Sustainability Report • {study?.study_name}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={downloadTemplate} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700">
              <Download className="w-4 h-4" /> Download Template
            </button>
            <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer ${
              uploadingExcel ? 'bg-yellow-500 text-white opacity-75' : 'bg-yellow-500 text-white hover:bg-yellow-600'
            }`}>
              {uploadingExcel ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload Excel Data
              <input type="file" accept=".xlsx,.xls" onChange={handleExcelImport} className="hidden" disabled={uploadingExcel} />
            </label>
            <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer ${
              scanningPdf ? 'bg-orange-500 text-white opacity-75' : 'bg-orange-500 text-white hover:bg-orange-600'
            }`}>
              {scanningPdf ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Scan PDF
              <input type="file" accept=".pdf" onChange={handlePdfScan} className="hidden" disabled={scanningPdf} />
            </label>
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save All
            </button>
            <button onClick={generateAIPoweredReport} disabled={generatingNarrative}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50">
              {generatingNarrative ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              AI Report
            </button>
            <button onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600">
              <Trash2 className="w-4 h-4" />
              Reset
            </button>
          </div>
        </div>

        {/* AI Report generation progress bar */}
        {generatingNarrative && reportProgress.progress > 0 && (
          <div className="mt-3 px-4 py-3 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm font-medium text-purple-800">{reportProgress.message || 'Generating report...'}</span>
              <span className="text-sm font-bold text-purple-800">{reportProgress.progress}%</span>
            </div>
            <div className="w-full bg-purple-200 rounded-full h-2.5">
              <div className="bg-purple-500 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${reportProgress.progress}%` }} />
            </div>
          </div>
        )}

        {/* PDF scan progress bar */}
        {scanningPdf && (
          <div className="mt-3 px-4 py-3 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm font-medium text-orange-800">{scanProgress.message || 'Scanning...'}</span>
              <span className="text-sm font-bold text-orange-800">{scanProgress.progress}%</span>
            </div>
            <div className="w-full bg-orange-200 rounded-full h-2.5">
              <div className="bg-orange-500 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${scanProgress.progress}%` }} />
            </div>
          </div>
        )}

        {/* Upload status banner */}
        {uploadStatus && (
          <div className={`mt-3 px-4 py-2 rounded-lg text-sm ${
            uploadStatus.status === 'success' ? 'bg-green-100 text-green-800 border border-green-200' :
            'bg-red-100 text-red-800 border border-red-200'
          }`}>
            <span className="font-medium">{uploadStatus.message}</span>
            {uploadStatus.sheets && (
              <span className="ml-2 text-xs opacity-75">Sections: {uploadStatus.sheets.join(', ')}</span>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-6">
        {/* Sidebar Navigation */}
        <div className="w-72 bg-white rounded-lg shadow-md p-3 flex-shrink-0">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 px-2">BRSR Sections</h2>
          {NAV_SECTIONS.map(section => {
            const SectionIcon = section.icon;
            const isExpanded = expandedSections[section.id];
            return (
              <div key={section.id} className="mb-1">
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left hover:bg-gray-100 transition-colors"
                >
                  <SectionIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-gray-800">{section.label}</div>
                    <div className="text-xs text-gray-500 truncate">{section.subtitle}</div>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                </button>
                {isExpanded && (
                  <div className="ml-4 mt-1 space-y-0.5">
                    {section.children.map(child => {
                      const ChildIcon = child.icon;
                      const isActive = activePanel === child.id;
                      return (
                        <button
                          key={child.id}
                          onClick={() => setActivePanel(child.id)}
                          className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-left text-sm transition-colors ${
                            isActive ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          <ChildIcon className={`w-3.5 h-3.5 flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                          <span className="truncate">{child.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Main Content */}
        <div className="flex-1 bg-white rounded-lg shadow-md p-6 min-w-0">
          {renderPanel()}

          {/* Save button at bottom */}
          <div className="mt-8 pt-4 border-t border-gray-200 flex justify-end">
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Data
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BRSRModule;
